'use client';

import { Box, Flex, Text } from '@radix-ui/themes';
import { SHOP_STEPS } from '@trashlab/sitesapp/src/components/shop/steps';
import { useIsMobile } from '@trashlab/sitesapp/src/hooks/use-mobile';
import {
  TLSiteShopStep,
  TLSiteShopStepsType,
} from '@trashlab/api/src/domains/consumer/types';

// Only show these specific steps in the stepper
const STEPPER_VISIBLE_STEP_KEYS: TLSiteShopStepsType[] = [
  TLSiteShopStep.Information,
  TLSiteShopStep.DeliveryAndPickup,
  TLSiteShopStep.Payment,
];

export function ShopStepper({
  currentStepIndex,
}: {
  currentStepIndex: number;
}) {
  const isMobile = useIsMobile();

  // Filter only the steps we want to display in the stepper UI
  const visibleSteps = SHOP_STEPS.filter((step) =>
    STEPPER_VISIBLE_STEP_KEYS.includes(step.key),
  );

  // Map the current step index to the correct visible step index
  let visibleStepIndex = 0;

  if (currentStepIndex >= 0 && currentStepIndex < SHOP_STEPS.length) {
    const currentStepKey = SHOP_STEPS[currentStepIndex].key;

    // Find the index in our visible steps array
    const mappedIndex = visibleSteps.findIndex(
      (step) => step.key === currentStepKey,
    );

    // If the current step is in our visible steps, use that index
    // Otherwise, use the last visible step that would be completed before the current step
    if (mappedIndex !== -1) {
      visibleStepIndex = mappedIndex;
    } else {
      // Find the appropriate fallback index
      for (let i = currentStepIndex; i >= 0; i--) {
        const stepKey = SHOP_STEPS[i].key;
        const visibleIndex = visibleSteps.findIndex(
          (step) => step.key === stepKey,
        );
        if (visibleIndex !== -1) {
          visibleStepIndex = visibleIndex;
          break;
        }
      }
    }
  }

  return (
    <Flex mt="4" className="flex-wrap sm:flex-nowrap gap-1 sm:gap-0">
      {visibleSteps.map((step, index) => {
        const isActive = index === visibleStepIndex;
        const isCompleted = index < visibleStepIndex;
        const isLast = index === visibleSteps.length - 1;

        return (
          <Flex
            key={step.key}
            className={`
              flex-1 relative min-h-[28px] sm:min-h-[32px]
              ${!isLast ? 'sm:-mr-[15px]' : ''} 
            `}
            style={{ zIndex: visibleSteps.length - index }}
          >
            <Box
              className={`
                w-full min-h-[28px] sm:min-h-[32px] flex items-center justify-center relative font-medium px-2 sm:px-3
                ${
                  isActive || isCompleted
                    ? 'bg-[var(--primary-color)]'
                    : 'bg-[var(--gray-3)]'
                }
                rounded-md sm:rounded-none
              `}
              style={{
                clipPath: isMobile
                  ? 'none'
                  : 'polygon(10px 0, 100% 0, 100% calc(100% - 10px), calc(100% - 10px) 100%, 0 100%, 0 10px)',
              }}
            >
              <Box
                className={`
                  w-5 h-5 sm:w-6 sm:h-5 min-w-[20px] rounded-full flex items-center justify-center mr-1.5 sm:mr-2 font-bold bg-white text-[var(--primary-color)] text-xs sm:text-sm
                `}
              >
                {index + 1}
              </Box>

              <Text
                size={isMobile ? '1' : '2'}
                className={`
                  whitespace-nowrap text-xs sm:text-sm
                  ${
                    isActive || isCompleted
                      ? 'font-bold text-white'
                      : 'text-[var(--primary-color)]'
                  }
                `}
              >
                {step.label}
              </Text>
            </Box>
          </Flex>
        );
      })}
    </Flex>
  );
}
